import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  randChanceBoolean,
  randPastDate,
  rand,
  randNumber,
} from '@ngneat/falso'

import { pageable, sortable, filterable, createErrorResponse } from './utils.js'
import type { MockServer, MirageServer } from './server.js'
import type { SchemaAttributes } from '../types.js'

export type FormAssignmentData = SchemaAttributes<'FormAssignment'>

export const model = (Model as ModelDefinition<FormAssignmentData>).extend({
  template: belongsTo('formTemplate'),
  createdUser: belongsTo('user'),
  patient: belongsTo('patient'),
})

export const factory = Factory.extend<FormAssignmentData>({
  reference_id() {
    return randNumber({ min: 1, max: 10000 })
  },
  created() {
    return randPastDate({ years: 1 }).toISOString()
  },
  status() {
    return rand(['SUBMITTED', 'ACCEPTED', 'DECLINED', 'CANCELLED'])
  },
  frozen() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
})

export function routes(server: MockServer) {
  server.get(
    '/form_assignments',
    function (this: MirageServer, schema, request) {
      const assignments = schema.all('formAssignment')

      const filtered = filterable(assignments, request, {
        context: (item, value) =>
          !value || item.template?.attrs?.context === value,
        reference_id: (item, value) =>
          !value || item.attrs.reference_id === Number(value),
        is_active: (item, value) => {
          if (value === undefined) {
            return true
          }
          const isActive = !item.attrs.frozen
          return value === 'true' ? isActive : !isActive
        },
      })

      const sorted = sortable(
        filtered,
        request,
        {
          name: (assignment) => assignment.template?.attrs?.name || '',
          created_user: (assignment) =>
            assignment.createdUser?.attrs?.first_name || '',
          created: (assignment) => assignment.attrs.created,
          status: (assignment) => assignment.attrs.status,
        },
        ['-id'],
      )

      const { data, meta } = pageable(sorted, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  server.post(
    '/form_assignments',
    function (this: MirageServer, schema, request) {
      const { data } = JSON.parse(request.requestBody)
      const { attributes, relationships } = data

      const template = relationships?.template?.data?.id
        ? schema.find('formTemplate', relationships.template.data.id)
        : null

      if (!template) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Template is required' }],
        })
      }

      const assignment = schema.create('formAssignment', {
        ...attributes,
        name: template.attrs.name, // Copy name from template
        created: new Date().toISOString(), // Set to current time
        status: null, // New assignments start with no status
        frozen: false,
        template,
        createdUser: schema.all('user').models[0] || null, // Default to first user
        patient: relationships?.patient?.data?.id
          ? schema.find('patient', relationships.patient.data.id)
          : null,
      })

      return assignment
    },
  )

  server.patch(
    '/form_assignments/:id/archive',
    function (this: MirageServer, schema, request) {
      const assignment = schema.find('formAssignment', request.params.id)
      if (!assignment) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Form assignment not found' }],
        })
      }

      assignment.update({ frozen: true })
      return assignment
    },
  )
}

export function seeds(server: MockServer) {
  // Create form assignments for consultations
  const consultations = server.schema.all('consultation').models

  consultations.forEach((consultation) => {
    // Get templates with 'consultation' context
    const consultationTemplates = server.schema
      .all('formTemplate')
      .models.filter((template) => template.attrs.context === 'consultation')

    if (consultationTemplates.length === 0) {
      return
    }

    // Get the consultation's patient and user
    const consultationPatient = consultation.patients?.models?.[0] || null
    const users = server.schema.all('user').models || []

    // Create 2-4 form assignments per consultation
    const assignmentCount = randNumber({ min: 2, max: 4 })

    for (let i = 0; i < assignmentCount; i++) {
      const template = rand(consultationTemplates)
      server.create('formAssignment', {
        name: template.attrs.name, // Copy name from template
        template,
        createdUser: rand(users),
        patient: consultationPatient,
        frozen: false,
        reference_id: Number(consultation.id),
      })
    }
  })

  // Create discharge note assignment
  const dischargeTemplates = server.schema
    .all('formTemplate')
    .models.filter((template) => template.attrs.context === 'discharge_note')

  if (dischargeTemplates.length > 0) {
    // Use discharge instructions as reference for discharge note assignments
    const dischargeInstructions = server.schema.all(
      'consultationDischargeinstruction',
    ).models

    dischargeInstructions.forEach((instruction) => {
      const template = rand(dischargeTemplates)
      server.create('formAssignment', {
        name: template.attrs.name, // Copy name from template
        template,
        createdUser: instruction.createdUser || instruction.modifiedUser,
        patient: instruction.patient,
        reference_id: Number(instruction.id),
      })
    })
  }
}
